/**
 * Receive workflow UI tokens: badge colors for FC transfer line aggregates and line-level status.
 * Use with `<Badge variant="outline" className={fcTransferReceiveLineBadgeClassNames.pending} />`
 * (or `inProgress` / `saved`). Same strings apply anywhere counts or row chips mirror these buckets.
 */

/** Canonical transfer line `status` strings from the inventory API / tech spec §4.2 */
export const FC_TRANSFER_LINE_STATUS = Object.freeze({
    PENDING: 'PENDING',
    IN_RECEIPT: 'IN_RECEIPT',
    COMPLETED: 'COMPLETED',
    COMPLETED_WITH_EXCEPTION: 'COMPLETED_WITH_EXCEPTION',
});

/**
 * Receive-flow badge buckets (subheader counts, table chips, etc.).
 *
 * @typedef {'pending' | 'inProgress' | 'saved'} FcTransferReceiveLineBadgeBucket
 */

/** Tailwind class lists paired with {@link FC_TRANSFER_LINE_STATUS} buckets above. */
export const fcTransferReceiveLineBadgeClassNames = Object.freeze({
    pending:
        'border-0 bg-zinc-300 font-normal text-foreground shadow-none hover:bg-zinc-300 dark:bg-zinc-600 dark:text-zinc-50 dark:hover:bg-zinc-600',
    inProgress:
        'border-0 bg-blue-100 font-normal text-blue-900 shadow-none hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-100 dark:hover:bg-blue-950',
    saved: 'border-0 bg-orange-200/90 font-normal text-orange-950 shadow-none hover:bg-orange-200/90 dark:bg-orange-950/50 dark:text-orange-100 dark:hover:bg-orange-950/50',
});

/**
 * Maps an API line `status` to a receive badge styling bucket.
 *
 * @param {unknown} lineStatus
 * @returns {FcTransferReceiveLineBadgeBucket | null}
 */
export function getFcTransferReceiveLineBadgeBucket(lineStatus) {
    const s = String(lineStatus ?? '').toUpperCase();
    if (s === FC_TRANSFER_LINE_STATUS.PENDING) {
        return 'pending';
    }
    if (s === FC_TRANSFER_LINE_STATUS.IN_RECEIPT) {
        return 'inProgress';
    }
    if (s === FC_TRANSFER_LINE_STATUS.COMPLETED || s === FC_TRANSFER_LINE_STATUS.COMPLETED_WITH_EXCEPTION) {
        return 'saved';
    }
    return null;
}

/**
 * Full `Badge` className for a line row from API status, or `undefined` when unknown.
 *
 * @param {unknown} lineStatus
 * @returns {string | undefined}
 */
export function getFcTransferReceiveLineBadgeClassName(lineStatus) {
    const bucket = getFcTransferReceiveLineBadgeBucket(lineStatus);
    return bucket ? fcTransferReceiveLineBadgeClassNames[bucket] : undefined;
}
